import React, { useState, useMemo } from 'react';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { 
  FileEarmarkText, 
  Building, 
  People, 
  ChevronRight,
  Award
} from 'react-bootstrap-icons';

import { useLanguage } from '../../../../API/Context/LanguageContext';
import { VocabButtons as btn_vocab } from '../../../../ConfigFiles/multilingual/VocabButtons';
import VocabProMenu from '../../../../ConfigFiles/multilingual/VocabProMenu';

// Sub-menus
import QualificationsWidget from './submenus/qualificationsWidget';
import SkillsWidget from './submenus/skillsWidget';
import WorkAssignmentWidget from './submenus/workAssignmentWidget';
import AssistantManagementWidget from './submenus/AssistantManagementWidget';

import './SlidingWidgetMenu.css';

interface MenuOption {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

export const ProfessionalMenu: React.FC = () => {
  const { t } = useLanguage();
  const [activeWidgetKey, setActiveWidgetKey] = useState<string | null>(null);

  const handleBack = () => setActiveWidgetKey(null);

  // Directory of sub-menus
  const menuOptions = useMemo<Record<string, MenuOption>>(() => ({
    practiceDetails: {
      id: 'practiceDetails',
      title: t(VocabProMenu, 'practice_details'),
      description: t(VocabProMenu, 'practice_details_desc'),
      icon: <Building size={24} className="text-primary" />,
      component: <WorkAssignmentWidget onSaveSuccess={handleBack} />,
    },
    assistants: {
      id: 'assistants',
      title: t(VocabProMenu, 'assistants'),
      description: t(VocabProMenu, 'assistants_desc'),
      icon: <People size={24} className="text-primary" />,
      component: <AssistantManagementWidget onSaveSuccess={handleBack} />,
    },
    skills: {
      id: 'skills',
      title: t(VocabProMenu, 'skills'),
      description: t(VocabProMenu, 'skills_desc'),
      icon: <Award size={24} className="text-primary" />,
      component: <SkillsWidget onSaveSuccess={handleBack} />,
    },
    qualifications: {
      id: 'qualifications',
      title: t(VocabProMenu, 'qualifications'),
      description: t(VocabProMenu, 'qualifications_desc'),
      icon: <FileEarmarkText size={24} className="text-primary" />,
      component: <QualificationsWidget onSaveSuccess={handleBack} />,
    },
  }), [t]);

  const activeOption = activeWidgetKey ? menuOptions[activeWidgetKey] : null;

  return (
    <div className="widget-container overflow-hidden">
      <div className={`slide-panel ${activeWidgetKey ? 'slide-left' : ''}`}>
        
        {/* PANEL 1: MENU DIRECTORY */}
        <div className="menu-panel p-3">
          <div className="mb-4">
            <h2 className="fw-bold m-0">{t(VocabProMenu, 'information_management')}</h2>
            <p className="text-muted small">
              Select a category to view or edit your professional preferences.
            </p>
          </div>

          <Row className="g-3">
            {Object.values(menuOptions).map((option) => (
              <Col xs={12} key={option.id}>
                <Card 
                  className="shadow-sm border-0 border-start border-4 border-primary rounded-3 hover-card cursor-pointer"
                  onClick={() => setActiveWidgetKey(option.id)}
                  style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease', cursor: 'pointer' }}
                >
                  <Card.Body className="d-flex align-items-center justify-content-between p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-2 bg-light rounded-circle d-flex align-items-center justify-content-center">
                        {option.icon}
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 text-dark">{option.title}</h6>
                        <small className="text-muted d-block">{option.description}</small>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-muted ms-2" />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* PANEL 2: ACTIVE SUB-MENU WIDGET */}
        <div className="widget-panel p-3">
          {/* Top Control Bar with Back Button on Left & Section Title on Right */}
          <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={handleBack} 
              className="d-flex align-items-center gap-2 rounded-pill px-3"
            >
              <span>{t(btn_vocab, 'back')}</span>
            </Button>

            {activeOption && (
              <h5 className="m-0 fw-bold text-dark">{activeOption.title}</h5>
            )}
          </div>

          {/* Submenu Widget Content */}
          <div className="active-widget-content">
            {activeOption?.component}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfessionalMenu;